<template>
  <div>
    <!-- Staged/uploaded files -->
    <ul v-if="stagedFiles.length" class="space-y-1.5 mb-2">
      <li
        v-for="file in stagedFiles"
        :key="file._uid"
        class="flex items-center gap-2.5 bg-gray-50 rounded-lg px-2.5 py-2"
      >
        <!-- A pasted screenshot is called pasted-screenshot-<id>-2.png. The
             thumbnail is the only way to tell one from another. -->
        <!-- A pasted screenshot is often of app data, so thumbnails are not
             shown while Demo Mode is on. -->
        <img
          v-if="file._preview"
          :src="file._preview"
          :alt="file.name"
          class="w-9 h-9 rounded object-cover border border-gray-200 bg-white shrink-0"
        />
        <span
          v-else
          class="w-9 h-9 rounded border border-gray-200 bg-white flex items-center justify-center shrink-0"
        >
          <FontAwesomeIcon :icon="['fas', fileIcon(file)]" class="text-gray-400" />
        </span>

        <div class="min-w-0 grow">
          <div class="flex items-baseline gap-2">
            <span class="text-sm text-gray-700 truncate">{{ file.name }}</span>
            <span class="text-xs text-gray-400 shrink-0">{{ formatSize(file.size) }}</span>
          </div>

          <!-- Every row says where it stands. Silence is what let a
               100%-failure bug go unreported for two months. -->
          <div v-if="file._uploading" class="flex items-center gap-1.5 mt-1">
            <div class="w-28 h-1 bg-gray-200 rounded-full overflow-hidden">
              <div
                class="h-full bg-primary-500 rounded-full transition-all"
                :style="{ width: (file._progress || 0) + '%' }"
              ></div>
            </div>
            <span class="text-[11px] text-gray-400">Attaching… {{ file._progress || 0 }}%</span>
          </div>
          <p v-else-if="file._uploaded" class="text-[11px] text-emerald-600 mt-0.5">
            <FontAwesomeIcon :icon="['fas', 'check']" class="mr-1" />Attached
          </p>
          <p v-else-if="file._error" class="text-[11px] text-red-600 mt-0.5" role="alert">
            {{ file._errorMessage || 'Upload failed.' }}
            <button
              v-if="effectiveRequirementId"
              class="underline hover:no-underline font-medium ml-1"
              @click="retryFile(file)"
            >
              Retry
            </button>
          </p>
          <p v-else class="text-[11px] text-gray-400 mt-0.5">
            Will attach when you {{ deferredVerb }}
          </p>
        </div>

        <button
          class="text-gray-400 hover:text-red-500 shrink-0 p-1"
          :aria-label="`Remove ${file.name}`"
          @click="removeFile(file)"
        >
          <FontAwesomeIcon :icon="['fas', 'times']" class="text-xs" />
        </button>
      </li>
    </ul>

    <!-- Drop zone / picker. Clickable, focusable, and keyboard-operable: a
         control you can tab to but not activate is a dead end. -->
    <div
      data-attachment-dropzone
      class="rounded-lg border-2 px-3 py-2.5 transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary-300"
      :class="dragOver ? 'border-primary-400 bg-primary-50' : 'border-dashed border-gray-200 hover:border-gray-300'"
      role="button"
      tabindex="0"
      :aria-label="`Attach files ${dropLabel}`"
      @click="openPicker"
      @keydown.enter.prevent="openPicker"
      @keydown.space.prevent="openPicker"
      @dragover.prevent="dragOver = true"
      @dragleave="dragOver = false"
      @drop.prevent="onDrop"
      @paste="handlePaste"
    >
      <div class="flex items-center justify-between gap-3">
        <span class="inline-flex items-center text-sm text-gray-600 min-w-0">
          <FontAwesomeIcon :icon="['fas', 'paperclip']" class="mr-1.5 text-gray-400 shrink-0" />
          <span class="font-medium shrink-0">Attach files</span>
          <span class="text-gray-400 ml-1.5 truncate">
            — drag them in, or paste a screenshot
          </span>
        </span>
        <span class="text-xs text-gray-400 shrink-0">
          {{ stagedFiles.length }}/{{ maxFiles }} · {{ formatSize(maxSizeBytes) }} max
        </span>
      </div>
      <p class="text-[11px] text-gray-400 mt-1">{{ ALLOWED_SUMMARY }}</p>
    </div>

    <!-- Outside the zone on purpose: openPicker() clicks this input, and a
         click from inside would bubble straight back into the zone's own
         @click and reopen the picker. -->
    <input
      ref="fileInput"
      type="file"
      class="hidden"
      multiple
      :accept="acceptString"
      @change="onFileSelect"
    />

    <!-- Validation errors, one line per rejected file — dropping three bad
         files and being told about one is its own confusion. -->
    <p
      v-for="(message, i) in validationErrors"
      :key="'v' + i"
      class="text-xs text-red-500 mt-1"
      role="alert"
    >
      {{ message }}
    </p>

    <!-- Whole-surface drop target. Without this, dropping a file an inch off
         the zone makes the browser navigate away to it and the half-written
         ticket is gone. pointer-events-none so the drop still lands. -->
    <Teleport to="body">
      <div
        v-if="surfaceDragActive"
        class="fixed inset-0 z-60 flex items-center justify-center bg-primary-500/10 pointer-events-none"
      >
        <div
          class="rounded-2xl border-2 border-dashed border-primary-400 bg-white/95 shadow-xl px-8 py-6 text-center"
        >
          <FontAwesomeIcon :icon="['fas', 'paperclip']" class="text-primary-500 text-2xl" />
          <p class="mt-2 text-sm font-medium text-gray-800">Drop to attach {{ dropLabel }}</p>
          <p class="text-[11px] text-gray-500 mt-1">
            {{ ALLOWED_SUMMARY }} · up to {{ formatSize(maxSizeBytes) }}
          </p>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
import { deleteAttachment } from '@/services/api/serviceDesk';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
  uploadRequirementMedia,
  uploadErrorMessage,
} from '@/utils/requirementMediaUpload';

// Keep in sync with MediaView.ALLOWED_EXTENSIONS (newCoBackend api/views.py)
// and product_management.models.ALLOWED_MEDIA_EXTENSIONS. Anything listed here
// but missing there fails server-side with "Unsupported file type".
const ALLOWED_EXTENSIONS = [
  '.jpg', '.jpeg', '.png', '.gif',
  '.doc', '.docx', '.pdf',
  '.ppt', '.pptx',
  '.xls', '.xlsx',
  '.txt', '.csv', '.zip',
];

// What the extension list means in words. Naming the types up front beats
// finding out by rejection.
const ALLOWED_SUMMARY =
  'Images, PDF, Word, Excel, PowerPoint, TXT, CSV, ZIP';

const IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.gif'];

// Clipboard image types we can name correctly. Anything else keeps its own
// subtype as the extension so the validation error names a real reason.
const PASTE_EXTENSIONS = {
  'image/png': '.png',
  'image/jpeg': '.jpg',
  'image/jpg': '.jpg',
  'image/gif': '.gif',
};

// Only the most recently mounted surface uploader owns document-level drag and
// paste, so a ticket drawer open behind a create modal can't also grab the
// drop. Module-level on purpose: it is one global surface being arbitrated.
const surfaceStack = [];

let uid = 0;

export default {
  name: 'RequirementAttachmentUpload',
  components: { FontAwesomeIcon },
  props: {
    requirementId: { type: [Number, String], default: null },
    maxFiles: { type: Number, default: 10 },
    maxSizeBytes: { type: Number, default: 10 * 1024 * 1024 },
    // Claim the whole window for drag-and-drop and paste. Set on the host that
    // owns the screen (ticket drawer, create modal); left off for an uploader
    // nested inside one, which keeps its own zone and its host's focus.
    surface: { type: Boolean, default: false },
    // Completes "Drop to attach ___" / "Will attach when you ___".
    dropLabel: { type: String, default: 'to this ticket' },
    deferredVerb: { type: String, default: 'submit' },
  },
  emits: ['update:mediaIds', 'files-changed', 'uploaded', 'busy'],
  data() {
    return {
      ALLOWED_SUMMARY,
      stagedFiles: [],
      mediaIds: [],
      dragOver: false,
      surfaceDragActive: false,
      dragDepth: 0,
      validationErrors: [],
      pasteCount: 0,
      lastBusy: false,
      // Set by uploadAllStaged when the parent creates the requirement after
      // the files were staged, so a failed row can still offer Retry.
      uploadRequirementId: null,
    };
  },
  computed: {
    acceptString() {
      return ALLOWED_EXTENSIONS.join(',');
    },
    effectiveRequirementId() {
      return this.requirementId || this.uploadRequirementId;
    },
  },
  watch: {
  },
  mounted() {
    if (!this.surface) return;
    surfaceStack.push(this);
    document.addEventListener('dragenter', this.onSurfaceDragEnter);
    document.addEventListener('dragover', this.onSurfaceDragOver);
    document.addEventListener('dragleave', this.onSurfaceDragLeave);
    document.addEventListener('drop', this.onSurfaceDrop);
    document.addEventListener('paste', this.onSurfacePaste);
  },
  beforeUnmount() {
    this.stagedFiles.forEach(this.revokePreview);
    if (!this.surface) return;
    const i = surfaceStack.indexOf(this);
    if (i !== -1) surfaceStack.splice(i, 1);
    document.removeEventListener('dragenter', this.onSurfaceDragEnter);
    document.removeEventListener('dragover', this.onSurfaceDragOver);
    document.removeEventListener('dragleave', this.onSurfaceDragLeave);
    document.removeEventListener('drop', this.onSurfaceDrop);
    document.removeEventListener('paste', this.onSurfacePaste);
  },
  methods: {
    openPicker() {
      this.$refs.fileInput?.click();
    },
    onFileSelect(event) {
      const files = Array.from(event.target.files);
      this.addFiles(files);
      this.$refs.fileInput.value = '';
    },
    onDrop(event) {
      this.dragOver = false;
      this.addFiles(Array.from(event.dataTransfer.files));
    },

    // --- whole-surface drag and paste -----------------------------------
    isSurfaceOwner() {
      return surfaceStack[surfaceStack.length - 1] === this;
    },
    draggingFiles(dataTransfer) {
      return Array.from(dataTransfer?.types || []).includes('Files');
    },
    onSurfaceDragEnter(event) {
      if (!this.isSurfaceOwner() || !this.draggingFiles(event.dataTransfer)) return;
      this.dragDepth += 1;
      this.surfaceDragActive = true;
    },
    onSurfaceDragOver(event) {
      if (!this.isSurfaceOwner() || !this.draggingFiles(event.dataTransfer)) return;
      // Required, and the whole point: without it the browser handles the drop
      // itself and navigates away from the app.
      event.preventDefault();
    },
    onSurfaceDragLeave(event) {
      if (!this.isSurfaceOwner() || !this.draggingFiles(event.dataTransfer)) return;
      this.dragDepth = Math.max(0, this.dragDepth - 1);
      if (!this.dragDepth) this.surfaceDragActive = false;
    },
    onSurfaceDrop(event) {
      if (!this.isSurfaceOwner()) return;
      // A nearer drop zone (the comment composer's) already took it. Read this
      // before our own preventDefault below.
      const handledNearer = event.defaultPrevented;
      this.dragDepth = 0;
      this.surfaceDragActive = false;
      if (!this.draggingFiles(event.dataTransfer)) return;
      event.preventDefault();
      if (handledNearer) return;
      this.addFiles(Array.from(event.dataTransfer.files));
    },
    onSurfacePaste(event) {
      // Same rule as drop: whatever is closest to the user's focus wins.
      if (!this.isSurfaceOwner() || event.defaultPrevented) return;
      this.handlePaste(event);
    },

    // Pull image files off a paste. Public so a host can forward its own paste
    // event (the comment composer's textarea holds focus, not us).
    handlePaste(event) {
      const items = Array.from(event.clipboardData?.items || []);
      const images = items.filter(
        it => it.kind === 'file' && it.type.startsWith('image/')
      );
      if (!images.length) return;
      // Only swallow the event when we actually took something, so pasting
      // text still behaves normally — and so a surface handler further out
      // knows to stand down.
      event.preventDefault();

      const files = [];
      for (const item of images) {
        const blob = item.getAsFile();
        if (!blob) continue;
        this.pasteCount += 1;
        // The extension check below is `'.' + name.split('.').pop()`, so a
        // generated name without a real extension would be rejected.
        const ext = PASTE_EXTENSIONS[blob.type] || '.' + blob.type.split('/')[1];
        const ticket = this.effectiveRequirementId || 'new';
        const name = `pasted-screenshot-${ticket}-${this.pasteCount}${ext}`;
        files.push(new File([blob], name, { type: blob.type }));
      }
      this.addFiles(files);
    },

    addFiles(files) {
      this.validationErrors = [];

      for (const file of files) {
        if (this.stagedFiles.length >= this.maxFiles) {
          this.validationErrors.push(`Maximum ${this.maxFiles} files allowed.`);
          break;
        }
        if (file.size > this.maxSizeBytes) {
          this.validationErrors.push(
            `"${file.name}" is ${this.formatSize(file.size)} — the limit is ${this.formatSize(this.maxSizeBytes)}.`
          );
          continue;
        }
        const ext = '.' + file.name.split('.').pop().toLowerCase();
        if (!ALLOWED_EXTENSIONS.includes(ext)) {
          this.validationErrors.push(
            `"${file.name}" isn't a supported type. ${ALLOWED_SUMMARY}.`
          );
          continue;
        }
        uid += 1;
        const entry = Object.assign(file, {
          _uid: uid,
          _uploading: false,
          _uploaded: false,
          _error: false,
          _errorMessage: '',
          _mediaId: null,
          _preview: this.previewUrl(file),
        });
        this.stagedFiles.push(entry);

        if (this.requirementId) {
          this.uploadFile(entry);
        }
      }
      this.$emit('files-changed', this.stagedFiles);
    },

    // Single upload path for every caller: picked, dropped, pasted, retried,
    // and the create-modal's deferred batch.
    async _upload(file, requirementId) {
      file._uploading = true;
      file._progress = 0;
      file._error = false;
      file._errorMessage = '';
      this.touch();
      try {
        const media = await uploadRequirementMedia({
          requirementId,
          file,
          onProgress: pct => {
            file._progress = pct;
            this.touch();
          },
        });
        file._uploading = false;
        file._uploaded = true;
        file._mediaId = media.id;
        this.$emit('uploaded', media);
        return media;
      } catch (err) {
        file._uploading = false;
        file._error = true;
        file._errorMessage = uploadErrorMessage(err);
        throw err;
      } finally {
        this.touch();
      }
    },
    async uploadFile(file) {
      try {
        const media = await this._upload(file, this.requirementId);
        this.mediaIds.push(media.id);
        this.$emit('update:mediaIds', [...this.mediaIds]);
      } catch {
        // Already surfaced on the row by _upload.
      }
    },
    async retryFile(file) {
      const requirementId = this.effectiveRequirementId;
      if (!file || !requirementId) return;
      try {
        const media = await this._upload(file, requirementId);
        if (!this.mediaIds.includes(media.id)) {
          this.mediaIds.push(media.id);
          this.$emit('update:mediaIds', [...this.mediaIds]);
        }
      } catch {
        // Already surfaced on the row by _upload.
      }
    },
    async uploadAllStaged(requirementId) {
      this.uploadRequirementId = requirementId;
      const results = [];
      for (const file of this.stagedFiles) {
        if (file._uploaded && file._mediaId) {
          results.push(file._mediaId);
          continue;
        }
        try {
          const media = await this._upload(file, requirementId);
          results.push(media.id);
        } catch {
          // Already surfaced on the row by _upload. Keep going so one bad
          // file doesn't strand the rest.
        }
      }
      this.mediaIds = results;
      this.$emit('update:mediaIds', [...results]);
      return results;
    },

    removeFile(file) {
      const index = this.stagedFiles.indexOf(file);
      if (index === -1) return;
      // The X means "I didn't mean to attach that". If it already reached the
      // server, take it back down rather than leaving an orphan on the ticket.
      // destroy is admin/security-only server-side; for everyone else this is
      // a no-op and the row simply leaves the composer.
      if (file._mediaId) {
        this.mediaIds = this.mediaIds.filter((id) => id !== file._mediaId);
        this.$emit('update:mediaIds', [...this.mediaIds]);
        deleteAttachment(file._mediaId).catch(() => {});
      }
      this.revokePreview(file);
      this.stagedFiles.splice(index, 1);
      this.$emit('files-changed', this.stagedFiles);
    },
    previewUrl(file) {
      const ext = '.' + file.name.split('.').pop().toLowerCase();
      if (!IMAGE_EXTENSIONS.includes(ext)) return null;
      return URL.createObjectURL(file);
    },
    revokePreview(file) {
      if (file?._preview) {
        URL.revokeObjectURL(file._preview);
        file._preview = null;
      }
    },

    // File objects aren't reactive in Vue 3 (reactive() skips them), so the
    // per-file flags above only reach the template when the array identity
    // changes.
    touch() {
      this.stagedFiles = [...this.stagedFiles];
      const busy = this.hasPending();
      if (busy !== this.lastBusy) {
        this.lastBusy = busy;
        this.$emit('busy', busy);
      }
    },
    getStagedFiles() {
      return this.stagedFiles;
    },
    getMediaIds() {
      return [...this.mediaIds];
    },
    hasFiles() {
      return this.stagedFiles.length > 0;
    },
    hasPending() {
      return this.stagedFiles.some((f) => f._uploading);
    },
    // Drop the rows without touching what was already uploaded — the file has
    // moved on to the attachment list or the comment it was posted with.
    clear() {
      this.stagedFiles.forEach(this.revokePreview);
      this.stagedFiles = [];
      this.mediaIds = [];
      this.validationErrors = [];
      this.$emit('update:mediaIds', []);
      this.$emit('files-changed', []);
    },
    fileIcon(file) {
      const type = file.type || '';
      if (type.includes('pdf')) return 'file-pdf';
      if (type.includes('image')) return 'file-image';
      if (type.includes('word')) return 'file-word';
      if (type.includes('sheet') || type.includes('excel') || type.includes('csv'))
        return 'file-excel';
      if (type.includes('presentation') || type.includes('powerpoint'))
        return 'file-powerpoint';
      if (type.includes('zip')) return 'file-zipper';
      return 'file';
    },
    formatSize(bytes) {
      if (!bytes) return '';
      if (bytes < 1024) return bytes + ' B';
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(0) + ' KB';
      return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    },
  },
};
</script>
